"use client";

import React from "react";
import usePrizeStraplineTexts from "../../hooks/usePrizeStraplineTexts";

/**
 * Trust figures that close out /prizes/lifestyle, below the BOTB Pass widget.
 *
 * The two CMS-driven values come from the same endpoint /botb-pass uses for
 * its own specs strip. The other two are fixed in the design, as they are
 * there. This is a separate component rather than a reuse of `Specs` because
 * the 2026 design regroups the four items into a 2x2 grid on mobile, where
 * /botb-pass still runs them across in a single row.
 *
 * Mobile dividers are interrupted (Figma): a 12px column gap breaks the
 * horizontal rules, and short vertical rules sit in that gap — they never
 * form a continuous cross. Desktop keeps the 3px bars between items.
 */
export default function TrustSpecs() {
  const straplines = usePrizeStraplineTexts();

  const specs = [
    {
      icon: "/assets/images/subscriptions/top-specs-1.webp",
      value: straplines.PrizeStraplineText1 && `${straplines.PrizeStraplineText1}y+`,
      label: "UK’s No. 1",
    },
    { icon: "/assets/images/subscriptions/top-specs-2.webp", value: "4.8 +", label: "App Store Rating" },
    {
      icon: "/assets/images/subscriptions/top-specs-3.webp",
      value: straplines.PrizeStraplineText2,
      label: "In Prizes Given",
    },
    { icon: "/assets/images/subscriptions/top-specs-4.webp", value: "2.7M", label: "#1 Community of Prize Lovers" },
  ];

  const [topLeft, topRight, bottomLeft, bottomRight] = specs;

  return (
    <section className="flex flex-col w-full pt-4 lg:pt-6 pb-2 font-jost">
      <div className="w-full px-4 lg:px-5">
        <div className="w-full max-w-[1320px] mx-auto">
          {/* Mobile: two rows with a 12px gap so borders don't meet at the centre. */}
          <div className="flex lg:hidden flex-col w-full px-6 py-3 bg-white rounded-lg">
            <div className="flex items-start w-full gap-3">
              <SpecCell spec={topLeft} className="flex-1 min-w-0 border-b border-solid border-black/8 pb-3" />
              <div className="flex items-center justify-center self-stretch shrink-0 w-0" aria-hidden="true">
                <div className="w-px h-[66px] bg-black/8" />
              </div>
              <SpecCell spec={topRight} className="flex-1 min-w-0 border-b border-solid border-black/8 pb-3" />
            </div>
            <div className="flex items-start w-full gap-3">
              <SpecCell spec={bottomLeft} className="flex-1 min-w-0 pt-3" />
              <div className="flex items-end justify-center self-stretch shrink-0 w-0" aria-hidden="true">
                <div className="w-px h-20 bg-black/8" />
              </div>
              <SpecCell spec={bottomRight} className="flex-1 min-w-0 pt-3" />
            </div>
          </div>

          {/* Desktop: single row with 3px bars between items. */}
          <div className="hidden lg:flex items-center justify-center w-full px-10 py-9 gap-[43px] bg-white rounded-lg">
            {specs.map((spec, index) => (
              <React.Fragment key={spec.label}>
                <SpecCell spec={spec} layout="desktop" />
                {index < specs.length - 1 && <div className="w-[3px] h-10 bg-black/8" aria-hidden="true" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function SpecCell({
  spec,
  className = "",
  layout = "mobile",
}: {
  spec: { icon: string; value: string | false | undefined; label: string };
  className?: string;
  layout?: "mobile" | "desktop";
}) {
  const isDesktop = layout === "desktop";

  return (
    <div className={`flex ${isDesktop ? "flex-row items-center gap-2" : "flex-col items-start gap-1"} ${className}`}>
      <img
        src={spec.icon}
        alt=""
        aria-hidden="true"
        width={48}
        height={48}
        loading="lazy"
        className={`shrink-0 ${isDesktop ? "size-12" : "size-8"}`}
      />
      <div className="min-w-0">
        <div className={`font-semibold text-primary ${isDesktop ? "text-xl leading-7" : "text-sm leading-5"}`}>
          {spec.value}
        </div>
        <div className={`font-medium text-secondary ${isDesktop ? "text-[13px] leading-[15px]" : "text-xs leading-3"}`}>
          {spec.label}
        </div>
      </div>
    </div>
  );
}
